import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Documentation from "./pages/Documentation";
import DataStandards from "./pages/DataStandards";
import DataStandardsBuilder from "./pages/DataStandardsBuilder";
import AdfStandard from "./pages/AdfStandard";
import AdfStandardBuilder from "./pages/AdfStandardBuilder";
import About from "./pages/About";
import UseCases from "./pages/UseCases";
import Partners from "./pages/Partners";
import Contact from "./pages/Contact";
import StandardsProposals from "./pages/StandardsProposals";
import ResetPassword from "./pages/ResetPassword";
import UpdatePassword from "./pages/UpdatePassword";
import AcceptInvite from "./pages/AcceptInvite";
import NotFound from "./pages/NotFound";

// New docs pages
import DocsHub from "./pages/docs/DocsHub";
import GuidesIndex from "./pages/docs/GuidesIndex";
import GuideArticle from "./pages/docs/GuideArticle";
import ApiDocs from "./pages/docs/ApiDocs";
import DataTypesIndex from "./pages/docs/DataTypesIndex";
import DataTypeArticle from "./pages/docs/DataTypeArticle";
import FieldLibraryIndex from "./pages/docs/FieldLibraryIndex";
import FieldArticle from "./pages/docs/FieldArticle";
import LookupsIndex from "./pages/docs/LookupsIndex";
import LookupGroupPage from "./pages/docs/LookupGroupPage";

const queryClient = new QueryClient();

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, isLoading, isAdminLoading } = useAuth();
  
  // Wait for both auth and admin role check to complete
  if (isLoading || isAdminLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/update-password" element={<UpdatePassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/api-standards" element={<DataStandards />} />
      <Route path="/api-standards/builder" element={<DataStandardsBuilder />} />
      <Route path="/adf-standard" element={<AdfStandard />} />
      <Route path="/adf-standard/builder" element={<AdfStandardBuilder />} />
      {/* Admin routes with URL-based sections */}
      <Route path="/dashboard/*" element={<AdminRoute><Dashboard /></AdminRoute>} />
      
      {/* Redirect /admin to /dashboard */}
      <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
      <Route path="/admin/*" element={<Navigate to="/dashboard" replace />} />
      
      {/* Documentation routes */}
      <Route path="/docs" element={<DocsHub />} />
      <Route path="/docs/guides" element={<GuidesIndex />} />
      <Route path="/docs/guides/:slug" element={<GuideArticle />} />
      <Route path="/docs/api" element={<ApiDocs />} />
      <Route path="/docs/data-types" element={<DataTypesIndex />} />
      <Route path="/docs/data-types/:slug" element={<DataTypeArticle />} />
      <Route path="/docs/field-library" element={<FieldLibraryIndex />} />
      <Route path="/docs/field-library/:slug" element={<FieldArticle />} />
      <Route path="/docs/lookups" element={<LookupsIndex />} />
      <Route path="/docs/lookups/:group" element={<LookupGroupPage />} />
      
      {/* Legacy docs route - redirect to new hub */}
      <Route path="/docs/data-types-legacy" element={<Documentation />} />
      
      <Route path="/about" element={<About />} />
      <Route path="/use-cases" element={<UseCases />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/proposals" element={<StandardsProposals />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
